import { GradientButton } from "../GradientButton";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import { useCallback, useEffect, useState } from "react";
import { CircularProgress, Stack, Tooltip } from "@mui/material";
import { toast } from "react-toastify";
import { CustomToast } from "../CustomToast";
import { useNavigate } from "react-router-dom";
import {
  campaignsApi,
  useGetCampaignDetailsQuery,
  useStartAccountPlanGenerationMutation,
  useUpdateAccountPlanGenerationMutation,
} from "../../../api";
import { CampaignDetailsResponse } from "../../../types/apiTypes";
import { AccountPlanStatus, AppRoutes } from "../../../types/enums";
import { getErrorMessage } from "../../../utils/getErrorMessage";
import { CustomButton } from "../CustomButton";
import {
  setIsFetchTableMembers,
  setIsNotificationsOpen,
} from "../../../features/companySlice";

export const StartGenerateAccountPlanBtn = ({
  account_id,
  account,
  fullWidth,
  campaign_id,
}: {
  account_id: string;
  campaign_id: string;
  account?: CampaignDetailsResponse;
  fullWidth?: boolean;
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isGeneratingReport = !!account?.is_generating_account_plan;
  const hasReport = !!account?.has_account_plan;
  const [isGenerating, setIsGenerating] = useState<boolean>(isGeneratingReport);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [isCompleted, setIsCompleted] = useState<boolean>(hasReport);

  const { isEditor } = useAppSelector((state) => state.company.userRoles);

  const accountPlanStatus = useAppSelector(
    (state) => state.company.status.account_plan_status,
  );

  const { data: campaignDetails, isLoading: isCampaignDetailsLoading } =
    useGetCampaignDetailsQuery({ account_id: account_id! });

  useEffect(() => {
    setIsUpdating(
      campaignDetails?.account_plan_status === AccountPlanStatus.updating,
    );
  }, [campaignDetails?.account_plan_status]);

  const [startGeneration, { isLoading }] =
    useStartAccountPlanGenerationMutation();

  const [updateAccountPlan] = useUpdateAccountPlanGenerationMutation();

  useEffect(() => {
    setIsCompleted(hasReport);
    setIsGenerating(isGeneratingReport);
  }, [hasReport, isGeneratingReport]);

  useEffect(() => {
    if (accountPlanStatus?.account_id === account_id) {
      switch (accountPlanStatus.status) {
        case "in_progress":
          setIsGenerating(true);
          setIsCompleted(false);
          break;

        case "completed":
          setIsGenerating(false);
          setIsCompleted(true);
          break;

        case "failed":
          setIsGenerating(false);
          setIsCompleted(false);
          break;
      }
    }
  }, [accountPlanStatus, account_id]);

  const handleStartGeneration = useCallback(async () => {
    await startGeneration({
      account_id,
      campaign_id,
    })
      .unwrap()
      .then(() => {
        setIsGenerating(true);
      })
      .catch((err) => {
        toast.error(
          <CustomToast
            type="error"
            title="Failed to Start"
            text={
              getErrorMessage(err).globalError || "Failed to start generation"
            }
          />,
        );
      });

    dispatch(campaignsApi.util.invalidateTags(["activities"]));
    dispatch(setIsNotificationsOpen(true));
  }, [account_id, startGeneration, dispatch]);

  const handleViewPlan = useCallback(() => {
    navigate(`/${campaign_id}${AppRoutes.ACCOUNT_PLAN}/${account_id}`);
  }, [account_id, campaign_id, navigate]);

  const updateAccountPlanHandler = () => {
    setIsUpdating(true);

    updateAccountPlan({
      campaign_id,
      account_ids: [account_id],
    })
      .unwrap()
      .then(() => {
        dispatch(campaignsApi.util.invalidateTags(["activities", "company"]));
        dispatch(setIsFetchTableMembers(true));
      })
      .catch((err) => {
        toast.error(
          <CustomToast
            type="error"
            title="Failed to Update"
            text={
              getErrorMessage(err).globalError || "Failed to update generation"
            }
          />,
        );
        setIsUpdating(false);
      });
  };

  if (isCompleted) {
    return (
      <Stack flexDirection="row" gap="12px">
        <CustomButton
          onClick={handleViewPlan}
          variant="outlined"
          customColor="#FFFFFF"
          textColor="#BD4D00"
          customSize="fit"
          type="button"
          sx={{ borderColor: "#BD4D00", backgroundColor: "transparent" }}
        >
          Check Current Account Plan
        </CustomButton>
        {campaignDetails?.has_committee_changes && isEditor && (
          <GradientButton
            fullWidth={fullWidth}
            onClick={updateAccountPlanHandler}
            sx={{
              minWidth: fullWidth ? "auto" : "200px",
              whiteSpace: "nowrap",
            }}
            startIcon={
              (isUpdating || isCampaignDetailsLoading) && (
                <CircularProgress size={24} sx={{ color: "inherit" }} />
              )
            }
            disabled={isUpdating || isCampaignDetailsLoading}
          >
            Update Account Plan
          </GradientButton>
        )}
      </Stack>
    );
  }

  const button = (
    <GradientButton
      fullWidth={fullWidth}
      onClick={handleStartGeneration}
      disabled={isLoading || isGenerating}
      sx={{
        minWidth: fullWidth ? "auto" : "200px",
        whiteSpace: "nowrap",
      }}
      startIcon={
        (isLoading || isGenerating) && (
          <CircularProgress size={24} sx={{ color: "inherit" }} />
        )
      }
    >
      {isLoading || isGenerating
        ? "Preparing Account Plan"
        : "Get Target Account Plan"}
    </GradientButton>
  );

  if (isLoading || isGenerating) {
    return (
      <Tooltip
        placement="top"
        sx={{
          "& .MuiTooltip-tooltip": {
            background: "#596177E5",
            fontSize: "12px",
            lineHeight: "16px",
          },
        }}
        arrow
        title="Your account plan is in progress. We'll notify you by email when it's done."
      >
        <span
          style={{
            width: fullWidth ? "100%" : "auto",
            display: "inline-block",
          }}
        >
          {button}
        </span>
      </Tooltip>
    );
  }

  return button;
};
